import { ApiError } from './api'

/**
 * Classificação de falha de API — o núcleo de "erro é erro, não vazio".
 *
 * A SPA tinha dezenove `.catch(() => ({ items: [], total: 0 }))`. Com a API fora
 * do ar, a fila de moderação mostrava "nenhum anúncio aguardando" — igualzinho a
 * uma fila limpa. O moderador fecha a tela achando que não há trabalho. É a pior
 * forma de erro: a que parece sucesso.
 *
 * Módulo puro de propósito: quem decide precisa rodar sob `environment: node` no
 * teste, então nada aqui toca DOM. O que renderiza vive em `collection-load.ts`.
 */

export type FailureKind =
  | 'unauthorized'
  | 'forbidden'
  | 'request'
  | 'server'
  | 'unavailable'
  | 'offline'
  | 'unknown'

export type ApiFailure = {
  kind: FailureKind
  status: number | null
  code: string
  message: string
  /** Se repetir tem chance de dar outro resultado. 403 e 409 não têm. */
  retryable: boolean
}

const MENSAGEM: Record<FailureKind, string> = {
  unauthorized: 'Sua sessão expirou. Entre novamente.',
  forbidden: 'Você não tem permissão para ver isto.',
  request: 'Não foi possível concluir a solicitação.',
  server: 'O servidor falhou ao responder.',
  unavailable: 'O serviço está indisponível no momento. Tente de novo em instantes.',
  offline: 'Sem conexão com o servidor. Verifique a rede e tente de novo.',
  unknown: 'Não foi possível carregar. Tente de novo.'
}

/** `{ error: { code, message } }` é o contrato de erro do backend. */
function envelope_of(body: unknown): { code: string; message: string } | null {
  const erro = (body as any)?.error
  if (!erro) return null
  if (typeof erro === 'string') return { code: erro, message: erro }
  const code = String(erro.code || '').trim()
  const message = String(erro.message || '').trim()
  return code || message ? { code, message } : null
}

export function classify_api_failure(err: unknown): ApiFailure {
  if (!(err instanceof ApiError)) {
    // `fetch` rejeita SEM status quando não há rede. Tratar como 500 mandaria o
    // usuário procurar defeito no servidor quando o problema é o wifi dele.
    const offline = err instanceof TypeError
    const kind: FailureKind = offline ? 'offline' : 'unknown'
    return { kind, status: null, code: kind, message: MENSAGEM[kind], retryable: true }
  }

  const status = Number(err.status || 0)
  const env = envelope_of(err.body)

  if (status === 401) {
    return { kind: 'unauthorized', status, code: env?.code || 'unauthorized', message: MENSAGEM.unauthorized, retryable: false }
  }
  if (status === 403) {
    return {
      kind: 'forbidden',
      status,
      code: env?.code || 'forbidden',
      message: env?.message || MENSAGEM.forbidden,
      retryable: false
    }
  }

  // Sem envelope JSON o servidor não chegou a responder como aplicação: é o
  // Nginx/WAF do o1 devolvendo HTML. Antes isso caía na mensagem genérica
  // "Falha ao processar a solicitação" e dizia ao operador que ELE errou algo.
  if (!env) {
    if (status >= 500 || status === 0) {
      return { kind: 'unavailable', status, code: 'unavailable', message: MENSAGEM.unavailable, retryable: true }
    }
    return { kind: 'request', status, code: 'request_error', message: MENSAGEM.request, retryable: false }
  }

  if (status >= 500) {
    return { kind: 'server', status, code: env.code || 'internal_error', message: env.message || MENSAGEM.server, retryable: true }
  }
  return { kind: 'request', status, code: env.code || 'request_error', message: env.message || MENSAGEM.request, retryable: false }
}

export function failure_message(failure: ApiFailure): string {
  return failure.message || MENSAGEM[failure.kind] || MENSAGEM.unknown
}

/** Só 401 derruba a sessão local. 403 significa "logado, mas sem essa tag". */
export function should_clear_session(failure: ApiFailure): boolean {
  return failure.kind === 'unauthorized'
}

/**
 * Caminho de login preservando o destino.
 *
 * `next` volta como navegação: aceitar destino absoluto daria um
 * redirecionamento aberto assinado pela nossa própria tela de login. Só caminho
 * interno passa — e `//host` e `/\host` também são absolutos para o navegador.
 */
export function login_redirect_path(current: string): string {
  const destino = String(current || '')
  if (!destino.startsWith('/')) return '/login'
  if (destino.startsWith('//') || destino.startsWith('/\\')) return '/login'
  if (destino === '/login' || destino.startsWith('/login?')) return '/login'
  return `/login?next=${encodeURIComponent(destino)}`
}

/**
 * O cache de sessão da SPA **nunca** expirava: `refresh_auth` só refazia a
 * checagem com `force`. Tirar a permissão de alguém não tinha efeito na aba
 * aberta — a UI seguia mostrando menus e botões que o backend já negava.
 */
export function is_auth_cache_fresh(checked_at: number, now: number, ttl_ms: number): boolean {
  if (!checked_at) return false
  const idade = now - checked_at
  // Relógio que anda para trás (ajuste de horário, suspensão) daria idade
  // negativa e eternizaria o cache.
  if (idade < 0) return false
  return idade < ttl_ms
}

/**
 * Nem todo 401 é sessão expirada.
 *
 * `POST /api/auth/login` com senha errada responde 401 — mandar o usuário para
 * `/login` ali recarregaria a própria tela, apagando o formulário e a mensagem
 * de erro que ele precisa ler. O redirecionamento global é para a sessão que
 * caiu no meio do trabalho, não para a tentativa de entrar.
 */
export function should_redirect_to_login(request_path: string, current_path: string, failure: ApiFailure): boolean {
  if (failure.kind !== 'unauthorized') return false
  const req = String(request_path || '')
  if (req.startsWith('/api/auth/')) return false
  // F6.2 — o 401 do ANUNCIANTE não é sessão de operador expirada. É o estado
  // normal de qualquer visitante que ainda não entrou, e mandá-lo para o
  // `/login` levaria quem quer anunciar a uma tela que diz "Acesso
  // administrativo". O universo do anunciante tem login próprio e guarda própria.
  if (req.startsWith('/api/advertiser/')) return false
  const atual = String(current_path || '')
  return !(atual === '/login' || atual.startsWith('/login?'))
}
